import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import { NavLink } from 'react-router-dom';
import {
    Select,
    FormControl,
    Input,
    MenuItem,
    Drawer,
    Toolbar,
    List,
    ClickAwayListener,
    ListItem,
    Checkbox,
    TextField,
    Divider,
    Typography,
} from '@mui/material';
import { Autocomplete } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import clsx from 'clsx';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';

// Icons
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

// Ops_Leaders
import PersonPinIcon from '@mui/icons-material/PersonPin';
// Divisions
import BubbleChartIcon from '@mui/icons-material/BubbleChart';
// Company
import BusinessIcon from '@mui/icons-material/Business';
// SFDC Stages
import ClearAllIcon from '@mui/icons-material/ClearAll';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { COLUMN_OPTIONS } from './constants';

const PREFIX = 'ProjectSidebar';

const classes = {
    root: `${PREFIX}-root`,
    menuButton: `${PREFIX}-menuButton`,
    hide: `${PREFIX}-hide`,
    drawer: `${PREFIX}-drawer`,
    drawerOpen: `${PREFIX}-drawerOpen`,
    drawerClose: `${PREFIX}-drawerClose`,
    active: `${PREFIX}-active`,
    toolbar: `${PREFIX}-toolbar`,
    multicheckboxFilter: `${PREFIX}-multicheckboxFilter`
};

const Root = styled('div')(({ theme }) => ({
    [`& .${classes.root}`]: {
        zIndex: 9,
        color: '#d8d8d8'
    },
    [`& .${classes.menuButton}`]: {
        marginRight: theme.spacing(4.5), // Use theme spacing instead of hardcoded value
    },
    [`& .${classes.hide}`]: {
        display: 'none',
    },
    [`& .${classes.drawer}`]: {
        width: 70,
        flexShrink: 0,
        whiteSpace: 'nowrap',
    },
    [`& .${classes.drawerOpen}`]: {
        width: 400,
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
        overflowX: 'visible',
    },
    [`& .${classes.drawerClose}`]: {
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        overflowX: 'hidden',
        width: theme.spacing(7) + 1,
        [theme.breakpoints.up('sm')]: {
            width: theme.spacing(7) + 1,
        },
    },
    [`& .${classes.active}`]: {
        backgroundColor: '#d8d8d8',
    },
    [`& .${classes.toolbar}`]: {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(6, 1, 0, 0),
        ...theme.mixins.toolbar,
    },
    [`& .${classes.multicheckboxFilter}`]: {
        width: '300px',
        paddingBottom: theme.spacing(2), // Use theme spacing
        display: 'inline-block',
    }
}));

const checkBoxOutlineIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkBoxIcon = <CheckBoxIcon fontSize="small" />;

const NavLinkMui = React.forwardRef((props, ref) => {
    return <NavLink {...props} ref={ref} activeclassname={classes.active} />;
});

// Reusable filter component
const FilterAutocomplete = React.memo(({ 
    id, 
    options, 
    defaultValue, 
    label, 
    placeholder, 
    onChange, 
    filterKey,
    filters 
}) => (
    <div className={classes.multicheckboxFilter}>
        <Autocomplete 
            multiple 
            id={id}
            options={options} 
            disableCloseOnSelect 
            defaultValue={defaultValue}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option?.title ?? '')}
            isOptionEqualToValue={(option, value) => {
              const o = typeof option === 'string' ? option : option?.title;
              const v = typeof value === 'string' ? value : value?.title;
              return o === v;
            }}
            onChange={onChange}
            renderOption={(props, option, { selected }) => (
                <li {...props}>
                    <Checkbox
                        icon={checkBoxOutlineIcon}
                        checkedIcon={checkBoxIcon}
                        style={{marginRight: 8}}
                        checked={selected}
                    />
                    {typeof option === 'string' ? option : option?.title}
                </li>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="outlined"
                    label={label}
                    placeholder={placeholder}
                />
            )} 
        />
    </div>
));

FilterAutocomplete.displayName = 'FilterAutocomplete';

FilterAutocomplete.propTypes = {
    id: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(PropTypes.shape({
        title: PropTypes.string.isRequired,
    })).isRequired,
    defaultValue: PropTypes.array,
    label: PropTypes.string.isRequired,
    placeholder: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    filterKey: PropTypes.string.isRequired,
    filters: PropTypes.object.isRequired,
};

const ProjectSidebar = (props) => {
    const {
        loading = false,
        filters = {},
        divisions = [],
        ops_leaders = [],
        stages = [],
        companies = [],
        updateFilters,
        showColumns = [],
        updateShowColumns
    } = props;


    const [open, setOpen] = React.useState(false);
    
    const handleDrawerOpen = useCallback(() => {
        setOpen(true);
    }, []);

    const handleDrawerClose = useCallback(() => {
        setOpen(false);
    }, []);

    // Memoized filter handlers for better performance
    const handleDivisionsChange = useCallback((e, value) => {
        updateFilters('divisions', value.map(a => a.title));
    }, [updateFilters]);

    const handleOpsLeadersChange = useCallback((e, value) => {
        updateFilters('ops_leaders', value.map(a => a.title));
    }, [updateFilters]);

    const handleStagesChange = useCallback((e, value) => {
        updateFilters('stages', value.map(a => a.title));
    }, [updateFilters]);

    const handleCompaniesChange = useCallback((e, value) => {
        updateFilters('companies', value.map(a => a.title));
    }, [updateFilters]);

    return (
        <Root>
            <div className={classes.root}>
                <CssBaseline />
                <Drawer
                    anchor={'right'}
                    variant="permanent"
                    role="navigation"
                    aria-label="Project filters and settings"
                    classes={{
                        paper: clsx(classes.drawer, {
                            [classes.drawerOpen]: open,
                            [classes.drawerClose]: !open,
                        }),
                    }}
                >
                    <Toolbar className={classes.toolbar}>
                        <List>
                                <br></br>
                                <ListItem
                                    button="true"
                                    key={'text'}
                                    aria-label="Open filter drawer"
                                    onClick={handleDrawerOpen}
                                    className={clsx({
                                        [classes.hide]: open,
                                    })}
                                >
                                    <ListItemIcon>
                                        <ChevronLeftIcon />
                                    </ListItemIcon>
                                    <ListItemText primary={'Views'} />
                                </ListItem>
                                <ListItem
                                    button="true"
                                    key={'text2'}
                                    aria-label="Close filter drawer"
                                    onClick={handleDrawerClose}
                                    className={clsx({
                                        [classes.hide]: !open,
                                    })}
                                >
                                    <ListItemIcon>
                                        <ChevronRightIcon />
                                    </ListItemIcon>
                                    <ListItemText primary={'Filters'} />
                                </ListItem>
                                
                                {/* Divisions */}
                                <ListItem
                                    key={'divisions'}
                                    className={classes.sublist}
                                    aria-label="Divisions filter section"
                                >
                                    <ListItemIcon>
                                        <BubbleChartIcon 
                                            onClick={handleDrawerOpen}
                                        />
                                    </ListItemIcon>
                                    {loading ? (<><br /><br />Loading...<br /><br /></>) : (
                                        <FilterAutocomplete
                                            id="divisions-filter"
                                            options={divisions}
                                            defaultValue={filters.divisions?.map(e => ({ title: e })) || []}
                                            label="Filter by Divisions"
                                            placeholder="Divisions"
                                            filterKey="divisions"
                                            filters={filters}
                                            onChange={handleDivisionsChange}
                                        />
                                    )}
                                </ListItem>                            

                                {/* OpsLeaders */}
                                <ListItem
                                    key={'ops_leaders'}
                                    className={classes.sublist}
                                    aria-label="Operations leaders filter section"
                                >
                                    <ListItemIcon>
                                        <PersonPinIcon onClick={handleDrawerOpen}/>
                                    </ListItemIcon>
                                    {loading ? (<><br /><br />Loading...<br /><br /></>) : (
                                        <FilterAutocomplete
                                            id="ops-leaders-filter"
                                            options={ops_leaders}
                                            defaultValue={filters.ops_leaders?.map(e => ({ title: e })) || []}
                                            label="Filter by OpsLeaders"
                                            placeholder="Ops Leader"
                                            filterKey="ops_leaders"
                                            filters={filters}
                                            onChange={handleOpsLeadersChange}
                                        />
                                    )}
                                </ListItem>                            

                                {/* SFDC Stages */}
                                <ListItem
                                    key={'stages'}
                                    className={classes.sublist}
                                    aria-label="SFDC stages filter section"
                                >
                                    <ListItemIcon>
                                        <ClearAllIcon onClick={handleDrawerOpen} />
                                    </ListItemIcon>
                                    {loading ? (<><br /><br />Loading...<br /><br /></>) : (
                                        <FilterAutocomplete
                                            id="stages-filter"
                                            options={stages}
                                            defaultValue={filters.stages?.map(e => ({ title: e })) || []}
                                            label="Filter by SFDC Stages"
                                            placeholder="Stage"
                                            filterKey="stages"
                                            filters={filters}
                                            onChange={handleStagesChange}
                                        />
                                    )}
                                </ListItem>                            

                                {/* Companies */}
                                <ListItem
                                    key={'companies'}
                                    className={classes.sublist}
                                    aria-label="Companies filter section"
                                >
                                    <ListItemIcon>
                                        <BusinessIcon onClick={handleDrawerOpen} />
                                    </ListItemIcon>
                                    {loading ? (<><br /><br />Loading...<br /><br /></>) : (
                                        <FilterAutocomplete
                                            id="companies-filter"
                                            options={companies}
                                            defaultValue={filters.companies?.map(e => ({ title: e })) || []}
                                            label="Filter by Companies"
                                            placeholder="Companies"
                                            filterKey="companies"
                                            filters={filters}
                                            onChange={handleCompaniesChange}
                                        />
                                    )}
                                </ListItem>                            

                                <Divider />
                                {/* Show/Hide Columns */}
                                &nbsp;

                                <ListItem
                                    key={'show/hide'}
                                    className={classes.sublist}
                                    aria-label="Show and hide columns section"
                                >
                                    <ListItemIcon>
                                        <CheckBoxIcon onClick={handleDrawerOpen} />
                                    </ListItemIcon>
                                    <div className={classes.multicheckboxFilter}>
                                        <FormControl className={classes.formControl}>
                                            <Select 
                                                id="show-hide-columns"
                                                multiple
                                                value={showColumns}
                                                onChange={(e) => {
                                                    updateShowColumns(e.target.value);
                                                    handleDrawerOpen();
                                                }}
                                                input={<Input />}
                                                renderValue={(selected) => "Show/Hide Columns"}
                                                MenuProps={{
                                                anchorOrigin: {
                                                    vertical: "bottom",
                                                    horizontal: "left"
                                                },
                                                transformOrigin: {
                                                    vertical: "top",
                                                    horizontal: "left"
                                                },
                                                getContentAnchorEl: null
                                                }}
                                            >
                                                {COLUMN_OPTIONS.map((name) => (
                                                <MenuItem key={name.title} value={name.column_id}>
                                                    <Checkbox checked={showColumns.indexOf(name.column_id) > -1} />
                                                    <ListItemText primary={name.title} />
                                                </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </div>
                                </ListItem>                            
                                <br />
                            
                                {/* Link to Edit Custom List */}
                                <ListItem
                                    button="true"
                                    key={'edit_custom_list'}
                                    className={classes.sublist}
                                    component={NavLinkMui}
                                    to="/custom-settings"
                                    aria-label="Navigate to custom settings page"
                                >
                                    <ListItemIcon>
                                        <BubbleChartIcon 
                                            onClick={handleDrawerOpen}
                                        />
                                    </ListItemIcon>
                                    <>
                                        <Typography>
                                            Edit Custom Settings
                                        </Typography>
                                        &nbsp;
                                        <OpenInNewIcon />
                                    </>
                                </ListItem>                            


                            </List>
                    </Toolbar>
                </Drawer>
            </div>
        </Root>
    );
};

ProjectSidebar.propTypes = {
    loading: PropTypes.bool,
    filters: PropTypes.shape({
        divisions: PropTypes.arrayOf(PropTypes.string),
        ops_leaders: PropTypes.arrayOf(PropTypes.string),
        stages: PropTypes.arrayOf(PropTypes.string),
        companies: PropTypes.arrayOf(PropTypes.string),
    }),
    divisions: PropTypes.arrayOf(PropTypes.shape({
        title: PropTypes.string.isRequired,
    })),
    ops_leaders: PropTypes.arrayOf(PropTypes.shape({
        title: PropTypes.string.isRequired,
    })),
    stages: PropTypes.arrayOf(PropTypes.shape({
        title: PropTypes.string.isRequired,
    })),
    companies: PropTypes.arrayOf(PropTypes.shape({
        title: PropTypes.string.isRequired,
    })),
    updateFilters: PropTypes.func.isRequired,
    showColumns: PropTypes.arrayOf(PropTypes.string),
    updateShowColumns: PropTypes.func.isRequired,
};

export default ProjectSidebar;